import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Flex, ProviderConsumer } from "@fluentui/react-northstar";
import Header from "../../organisms/Header";
import { observer } from "mobx-react-lite";
import MyTeamsList from "../../molecules/MyTeamsList";
import MyStatusContentComponent from "../../organisms/MyStatusContentComponent";
import TitleContentComponent from "../../organisms/TitleContentComponent";
import WelcomeDialog from "../../organisms/WelcomeDialog";
import CallDetails from "../../../components/molecules/CallDetails";
import AssistanceAlert from "../../atoms/AssistanceAlert";
import TeamMembers from '../../../components/molecules/TeamMembers';
import Settings from "../../organisms/Settings";
import { useMst } from "../../../hooks/useMst";
import AlertError from "../../atoms/Alert";
import Constants from "../../../constants/text-constants";
import Wootric from "../../atoms//Wootric";
import baseHttpHandler from "../../../services/api/utilities/baseHttpHandler";
import PermissionsHandler from "../../../services/api/permissionsHandler";
import { appInsights } from "../../../config/analytics/AppInsights";

const CallAssistantPageTemplate = observer((props) => {
  const {
    signOutClicked,
    settingsClicked,
    currentUser,
    statusDropdownItems,
    locations,
    setRegistered,
    setTeamRegistration,
    setStatus,
    setLocation,
    currentCallTeamId,
    setCurrentCallTeam,
    agents,
    showSettings,
    showWelcomeDialog,
    welcomeDialogComplete,
    firstname,
  } = props;

  let { permissionStore } = useMst();

  let teams = [];
  let showcallDetails =
    currentUser && currentUser.agent ? currentUser.agent.getIsInCall() : false;

  if (currentUser.agent && currentUser.agent.teams) {
    teams = currentUser.agent.teams;
  }

  const [displayAssistanceRequestAlert, setDisplayAssistanceRequestAlert] =
    useState(false);

  const showAlert = () => {
    setDisplayAssistanceRequestAlert(true);
  };

  const dismissAlert = () => {
    setDisplayAssistanceRequestAlert(false);
  };

  const [displayPermissionAlert, setDisplayPermissionAlert] = useState(false);

  useEffect(() => {
    function alertHandler(event) {
      // Ignore any alerts for the CustomValue endpoint
      setDisplayPermissionAlert(true);
        
        PermissionsHandler.getPermissions().then((response) => {
          permissionStore.setPermissions(response);
        }).catch((e) => {
          //getpermissions call threw an exception, not much we can do here
          appInsights.trackException({exception: e});
        });
    }

    baseHttpHandler.addAlertListener(alertHandler);
    return function cleanup() {
      baseHttpHandler.removeAlertListener(alertHandler);
    };
  });

  const dismissErrorAlert = () => {
    setDisplayPermissionAlert(false);
  }

  const renderCallDetails = () => {
    return (
      <TitleContentComponent
        title={"Call Details"}
        component={
          <CallDetails
            currentUser={currentUser}
            showAlert={showAlert}
            dismissAlert={dismissAlert}
          />
        }
        testId={"myCallDetailsContainer"}
      />
    );
  };

  const renderMyDetails = () => {
    return (
      <React.Fragment>
        <TitleContentComponent
          title={"My Teams"}
          component={
            <MyTeamsList
              teams={teams}
              setTeamRegistration={setTeamRegistration}
            />
          }
          testId={"myTeamsContainer"}
        />
        <TitleContentComponent
          title={"My Status"}
          component={
            <MyStatusContentComponent
              statusDropdownItems={statusDropdownItems}
              currentUser={currentUser}
              onChange={setStatus}
              currentCallTeamId={currentCallTeamId}
              setCurrentCallTeam={setCurrentCallTeam}
            />
          }
          testId={"myStatusContainer"}
        />
      </React.Fragment>
    );
  };

  const renderTeamMembers = () => {
    return (
      <TitleContentComponent
        title={Constants.TEAM_MEMBERS}
        component={
          <TeamMembers
            agents={agents}
          />
        }
        testId={"teamMembersContainer"}
      />
    );
  };

  const hasCMMonitorPerm = permissionStore.havePermissions("Call Manager", [
    "Monitor",
  ]);
  const displayName = firstname && firstname.length <= 30 ? firstname : null;

  return (
    <ProviderConsumer
      render={(theme) => (
        <Flex className="call-assistant-container">
          <Flex className="left-hand-container" column gap="gap.large">
            {displayAssistanceRequestAlert ? (
              <AssistanceAlert dismissAlert={dismissAlert} />
            ) : null}
            {displayPermissionAlert ? (
              <AlertError
                dismissAlert={dismissErrorAlert}
                title={Constants.PERMISSIONS_ERROR_HEADER}
                text={Constants.PERMISSIONS_ERROR_CONTENT}
                testId={"permissionAlert"}
              />
            ) : null}
            {showcallDetails ? renderCallDetails() : renderMyDetails()}
            {hasCMMonitorPerm ? renderTeamMembers() : null}
          </Flex>
          <Flex className="right-hand-container" column hAlign="end">
            <Header
              signOutClicked={signOutClicked}
              settingsClicked={settingsClicked}
              currentUser={currentUser}
              setRegistered={setRegistered}
              locations={locations}
              setLocation={setLocation}
            />
          </Flex>
          {showSettings ? (
            <Settings
              locations={locations}
              closeButtonClicked={settingsClicked}
            />
          ) : null}
          {showWelcomeDialog ? (
            <WelcomeDialog
              firstname={displayName}
              welcomeDialogComplete={welcomeDialogComplete}
            />
          ) : null}
          {process.env.REACT_APP_SHOW_WOOTRIC_SURVEY === "true" && (
            <Wootric theme={theme.siteVariables.themeString} />
          )}
        </Flex>
      )}
    ></ProviderConsumer>
  );
});

CallAssistantPageTemplate.propTypes = {
  signOutClicked: PropTypes.func,
  settingsClicked: PropTypes.func,
  setRegistered: PropTypes.func,
  setTeamRegistration: PropTypes.func,
  currentUser: PropTypes.object,
  setLocation: PropTypes.func,
  currentCallTeamId: PropTypes.number,
  setCurrentCallTeam: PropTypes.func,
  showSettings: PropTypes.bool,
  showWelcomeDialog: PropTypes.bool,
  welcomeDialogComplete: PropTypes.func,
  firstname: PropTypes.string,
};

CallAssistantPageTemplate.defaultProps = {
  currentUser: {},
  showSettings: false,
  showWelcomeDialog: false,
};

export default CallAssistantPageTemplate;
